/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IComponentProps } from '@msdyn365-commerce/core';
import { ProductList } from '@msdyn365-commerce/retail-proxy';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from '@msdyn365-commerce-modules/utilities';
import React, { useState } from 'react';

export interface IDeleteOrderTemplateDialogResources {
    deleteOrderTemplateHeader: string;
    deleteOrderTemplateDescription: string;
    deleteOrderTemplateButtonText: string;
    deleteOrderTemplateCancelButtonText: string;
    authenticationErrorMessage: string;
    deleteFailedErrorMessage: string;
}

export interface IDeleteOrderTemplateDialogProps extends IComponentProps {
    modalOpen: boolean | undefined;
    setModalOpen(newValue: boolean): void;
    productList: ProductList;
    onSuccess(orderTemplate: ProductList): Promise<void> | void;
    onCancel(): void;
    resources: IDeleteOrderTemplateDialogResources;
}

export const DeleteOrderTemplateDialog = (props: IDeleteOrderTemplateDialogProps) => {
    const { modalOpen, setModalOpen, resources, onSuccess, productList, context, onCancel } = props;
    const [isLoading, setIsLoading] = useState(false);
    const [deleteError, setDeleteError] = useState<Error | undefined>(undefined);

    const closeDialog = () => {
        if (isLoading) {
            return;
        }
        setDeleteError(undefined);
        setModalOpen(false);
        onCancel();
    };

    const clickConfirm = async () => {
        setIsLoading(true);
        try {
            if (!context || !context.request.user.isAuthenticated) {
                throw new Error(resources.authenticationErrorMessage);
            }
            await onSuccess(productList);
            setModalOpen(false);
        } catch (error) {
            context?.telemetry?.error('Failed to delete order template', error);
            setDeleteError(error instanceof Error ? error : new Error(String(error)));
        } finally {
            setIsLoading(false);
        }
    };

    const className = 'msc-order-template-delete-dialog';

    return (
        <>
            <Modal aria-labelledby='delete-order-template-header' isOpen={modalOpen} className={className} toggle={closeDialog}>
                <ModalHeader id='delete-order-template-header' className={`${className}__dialog__header`} toggle={closeDialog}>
                    <span>{resources.deleteOrderTemplateHeader}</span>
                </ModalHeader>
                <ModalBody className={`${className}__dialog__body`}>
                    <div className={`${className}__dialog__content`}>
                        <div className={`${className}__dialog__description`}>{resources.deleteOrderTemplateDescription}</div>
                        {deleteError && (
                            <div className={`${className}__dialog__error msc-alert-danger`} role='alert'>
                                {deleteError.message === resources.authenticationErrorMessage
                                    ? resources.authenticationErrorMessage
                                    : resources.deleteFailedErrorMessage}
                            </div>
                        )}
                    </div>
                </ModalBody>
                <ModalFooter className={`${className}__dialog__footer`}>
                    <Button
                        className={`${className}__dialog__delete-order-template-button`}
                        title={resources.deleteOrderTemplateButtonText}
                        onClick={clickConfirm}
                        disabled={isLoading}
                        aria-busy={isLoading}
                    >
                        {resources.deleteOrderTemplateButtonText}
                    </Button>
                    <Button
                        className={`${className}__dialog__cancel-button`}
                        title={resources.deleteOrderTemplateCancelButtonText}
                        onClick={closeDialog}
                        disabled={isLoading}
                    >
                        {resources.deleteOrderTemplateCancelButtonText}
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};
